import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { tenantWorkspaceApi } from "@/core/api/tenantWorkspaceApi";
import type {
  CatalogCategory,
  CatalogItem,
  CatalogVariant,
  TenantBranch,
  UpsertCatalogCategoryRequest,
  UpsertCatalogItemRequest,
  UpsertCatalogStockRequest,
  UpsertCatalogVariantRequest,
  UpsertTenantBranchRequest,
} from "@/core/types/tenantWorkspace";
import { AppIcon } from "@/shared/components/AppIcon";
import {
  catalogLinkProps,
  isPdfUrl,
  normalizeCatalogUrl,
  PdfViewerModal,
  resolveCatalogResourceUrl,
} from "@/shared/components/CatalogResource";
import { tenantBusiness } from "@/core/utils/tenantBusiness";

type ItemType = CatalogItem["itemType"];
type Availability = CatalogItem["availabilityStatus"];

interface ItemForm {
  name: string;
  description: string;
  catalogUrl: string;
  price: string;
  previousPrice: string;
  currency: string;
  categoryId: string;
  itemType: ItemType;
  code: string;
  barcode: string;
  brand: string;
  unit: string;
  availabilityStatus: Availability;
  isPublished: boolean;
  isFeatured: boolean;
  sortOrder: string;
}

interface CategoryForm {
  name: string;
  description: string;
  parentId: string;
  sortOrder: string;
  isActive: boolean;
}

const emptyItemForm = (currency = "IRR"): ItemForm => ({
  name: "",
  description: "",
  catalogUrl: "",
  price: "",
  previousPrice: "",
  currency,
  categoryId: "",
  itemType: "Product",
  code: "",
  barcode: "",
  brand: "",
  unit: "عدد",
  availabilityStatus: "Available",
  isPublished: true,
  isFeatured: false,
  sortOrder: "0",
});
const emptyCategoryForm = (): CategoryForm => ({
  name: "",
  description: "",
  parentId: "",
  sortOrder: "0",
  isActive: true,
});
const maximumFileSize = 15 * 1024 * 1024;
const acceptedTypes = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
];
const faNumber = (value: number): string =>
  new Intl.NumberFormat("fa-IR").format(value);
const errorText = (error: unknown): string =>
  error instanceof Error ? error.message : "عملیات ناموفق بود.";
const isImageUrl = (url: string | null): boolean =>
  Boolean(url && /\.(?:jpe?g|png|webp)(?:\?|$)/i.test(url));
const typeLabels: Record<ItemType, string> = {
  Product: "محصول",
  Service: "خدمت",
};
const availabilityLabels: Record<Availability, string> = {
  Available: "موجود",
  OnRequest: "استعلام قیمت",
  OutOfStock: "ناموجود",
  Discontinued: "متوقف شده",
};
const availabilityStyles: Record<Availability, string> = {
  Available: "bg-emerald-50 text-emerald-700",
  OnRequest: "bg-amber-50 text-amber-700",
  OutOfStock: "bg-rose-50 text-rose-700",
  Discontinued: "bg-slate-100 text-slate-600",
};

export const CatalogPage = (): React.ReactElement => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [itemEditor, setItemEditor] = useState<CatalogItem | "new" | null>(
    null,
  );
  const [categoryEditor, setCategoryEditor] = useState<
    CatalogCategory | "new" | null
  >(null);
  const [itemForm, setItemForm] = useState<ItemForm>(emptyItemForm);
  const [categoryForm, setCategoryForm] =
    useState<CategoryForm>(emptyCategoryForm);
  const [search, setSearch] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("all");
  const [selectedType, setSelectedType] = useState<"all" | ItemType>("all");
  const [selectedFileName, setSelectedFileName] = useState("");
  const [fileError, setFileError] = useState("");
  const [pdfPreview, setPdfPreview] = useState<CatalogItem | null>(null);
  const [inventoryProduct, setInventoryProduct] = useState<CatalogItem | null>(
    null,
  );
  const [branchesOpen, setBranchesOpen] = useState(false);

  const itemsQuery = useQuery({
    queryKey: ["tenant-workspace", "catalog"],
    queryFn: tenantWorkspaceApi.catalog,
  });
  const categoriesQuery = useQuery({
    queryKey: ["tenant-workspace", "catalog-categories"],
    queryFn: tenantWorkspaceApi.catalogCategories,
  });
  const profileQuery = useQuery({
    queryKey: ["tenant-workspace", "profile"],
    queryFn: tenantWorkspaceApi.profile,
  });
  const subscriptionQuery = useQuery({
    queryKey: ["tenant-workspace", "subscription"],
    queryFn: tenantWorkspaceApi.subscription,
  });
  const branchesQuery = useQuery({
    queryKey: ["tenant-workspace", "branches"],
    queryFn: tenantWorkspaceApi.branches,
  });
  const variantsQuery = useQuery({
    queryKey: ["tenant-workspace", "catalog-variants", inventoryProduct?.id],
    queryFn: () => tenantWorkspaceApi.catalogVariants(inventoryProduct!.id),
    enabled: Boolean(inventoryProduct),
  });
  const items = itemsQuery.data ?? [];
  const categories = categoriesQuery.data ?? [];
  const activeCategories = categories.filter((category) => category.isActive);
  const entityName = tenantBusiness(profileQuery.data?.business).entityName;
  const limit = subscriptionQuery.data?.maxCatalogItems ?? 0;
  const limitReached = limit > 0 && items.length >= limit;
  const filteredItems = useMemo(
    () =>
      items.filter((item) => {
        const haystack =
          `${item.name} ${item.code ?? ""} ${item.barcode ?? ""} ${item.brand ?? ""} ${item.categoryName ?? ""}`.toLocaleLowerCase(
            "fa",
          );
        return (
          (!search.trim() ||
            haystack.includes(search.trim().toLocaleLowerCase("fa"))) &&
          (selectedCategoryId === "all" ||
            item.categoryId === selectedCategoryId) &&
          (selectedType === "all" || item.itemType === selectedType)
        );
      }),
    [items, search, selectedCategoryId, selectedType],
  );
  const invalidate = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["tenant-workspace", "catalog"],
      }),
      queryClient.invalidateQueries({
        queryKey: ["tenant-workspace", "catalog-categories"],
      }),
    ]);
  };
  const invalidateInventory = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["tenant-workspace", "branches"],
      }),
      queryClient.invalidateQueries({
        queryKey: ["tenant-workspace", "catalog-variants"],
      }),
      queryClient.invalidateQueries({ queryKey: ["retail"] }),
    ]);
  };

  const uploadFile = useMutation({
    mutationFn: tenantWorkspaceApi.uploadCatalogFile,
    onSuccess: (uploaded) => {
      setItemForm((current) => ({
        ...current,
        catalogUrl: uploaded.url,
        name: current.name.trim() || uploaded.fileName.replace(/\.[^.]+$/, ""),
      }));
      setSelectedFileName(uploaded.fileName);
      setFileError("");
    },
  });
  const saveItem = useMutation({
    mutationFn: () => {
      const request: UpsertCatalogItemRequest = {
        name: itemForm.name.trim(),
        description: itemForm.description.trim() || null,
        catalogUrl: normalizeCatalogUrl(itemForm.catalogUrl) || null,
        price: itemForm.price ? Number(itemForm.price) : null,
        previousPrice: itemForm.previousPrice
          ? Number(itemForm.previousPrice)
          : null,
        currency: itemForm.currency,
        isPublished: itemForm.isPublished,
        categoryId: itemForm.categoryId || null,
        itemType: itemForm.itemType,
        code: itemForm.code.trim() || null,
        barcode: itemForm.barcode.trim() || null,
        brand: itemForm.brand.trim() || null,
        unit: itemForm.unit.trim() || "عدد",
        availabilityStatus: itemForm.availabilityStatus,
        isFeatured: itemForm.isFeatured,
        sortOrder: Number(itemForm.sortOrder) || 0,
      };
      return itemEditor === "new"
        ? tenantWorkspaceApi.createCatalogItem(request)
        : tenantWorkspaceApi.updateCatalogItem(itemEditor!.id, request);
    },
    onSuccess: async () => {
      closeItemEditor();
      await invalidate();
    },
  });
  const removeItem = useMutation({
    mutationFn: tenantWorkspaceApi.deleteCatalogItem,
    onSuccess: invalidate,
  });
  const saveCategory = useMutation({
    mutationFn: () => {
      const request: UpsertCatalogCategoryRequest = {
        name: categoryForm.name.trim(),
        description: categoryForm.description.trim() || null,
        parentId: categoryForm.parentId || null,
        sortOrder: Number(categoryForm.sortOrder) || 0,
        isActive: categoryForm.isActive,
      };
      return categoryEditor === "new"
        ? tenantWorkspaceApi.createCatalogCategory(request)
        : tenantWorkspaceApi.updateCatalogCategory(categoryEditor!.id, request);
    },
    onSuccess: async () => {
      closeCategoryEditor();
      await invalidate();
    },
  });
  const removeCategory = useMutation({
    mutationFn: tenantWorkspaceApi.deleteCatalogCategory,
    onSuccess: invalidate,
  });
  const saveBranch = useMutation({
    mutationFn: ({
      id,
      request,
    }: {
      id?: string;
      request: UpsertTenantBranchRequest;
    }) =>
      id
        ? tenantWorkspaceApi.updateBranch(id, request)
        : tenantWorkspaceApi.createBranch(request),
    onSuccess: invalidateInventory,
  });
  const removeBranch = useMutation({
    mutationFn: tenantWorkspaceApi.deleteBranch,
    onSuccess: invalidateInventory,
  });
  const saveVariant = useMutation({
    mutationFn: ({
      id,
      request,
    }: {
      id?: string;
      request: UpsertCatalogVariantRequest;
    }) =>
      id
        ? tenantWorkspaceApi.updateCatalogVariant(
            inventoryProduct!.id,
            id,
            request,
          )
        : tenantWorkspaceApi.createCatalogVariant(
            inventoryProduct!.id,
            request,
          ),
    onSuccess: invalidateInventory,
  });
  const removeVariant = useMutation({
    mutationFn: (variantId: string) =>
      tenantWorkspaceApi.deleteCatalogVariant(inventoryProduct!.id, variantId),
    onSuccess: invalidateInventory,
  });
  const saveStock = useMutation({
    mutationFn: ({
      variantId,
      request,
    }: {
      variantId: string;
      request: UpsertCatalogStockRequest;
    }) => tenantWorkspaceApi.updateCatalogStock(variantId, request),
    onSuccess: invalidateInventory,
  });

  const openItemEditor = (item?: CatalogItem): void => {
    setItemForm(
      item
        ? {
            name: item.name,
            description: item.description ?? "",
            catalogUrl: item.catalogUrl ?? "",
            price: item.price?.toString() ?? "",
            previousPrice: item.previousPrice?.toString() ?? "",
            currency: item.currency,
            categoryId: item.categoryId ?? "",
            itemType: item.itemType,
            code: item.code ?? "",
            barcode: item.barcode ?? "",
            brand: item.brand ?? "",
            unit: item.unit,
            availabilityStatus: item.availabilityStatus,
            isPublished: item.isPublished,
            isFeatured: item.isFeatured,
            sortOrder: String(item.sortOrder),
          }
        : emptyItemForm(profileQuery.data?.defaultCurrency || "IRR"),
    );
    setSelectedFileName("");
    setFileError("");
    uploadFile.reset();
    saveItem.reset();
    setItemEditor(item ?? "new");
  };
  const closeItemEditor = (): void => {
    setItemEditor(null);
    setItemForm(emptyItemForm());
    setSelectedFileName("");
    setFileError("");
  };
  const openCategoryEditor = (category?: CatalogCategory): void => {
    setCategoryForm(
      category
        ? {
            name: category.name,
            description: category.description ?? "",
            parentId: category.parentId ?? "",
            sortOrder: String(category.sortOrder),
            isActive: category.isActive,
          }
        : emptyCategoryForm(),
    );
    saveCategory.reset();
    setCategoryEditor(category ?? "new");
  };
  const closeCategoryEditor = (): void => {
    setCategoryEditor(null);
    setCategoryForm(emptyCategoryForm());
  };
  const chooseFile = (file?: File): void => {
    if (!file) return;
    if (!acceptedTypes.includes(file.type)) {
      setFileError("فقط PDF یا تصویر JPG، PNG و WebP قابل آپلود است.");
      return;
    }
    if (file.size > maximumFileSize) {
      setFileError("حجم فایل نباید بیشتر از ۱۵ مگابایت باشد.");
      return;
    }
    setSelectedFileName(file.name);
    setFileError("");
    uploadFile.mutate(file);
  };
  const parentOptions = categories.filter(
    (category) =>
      category.id !==
      (categoryEditor === "new" ? undefined : categoryEditor?.id),
  );
  const categoryTitle = (category: CatalogCategory): string =>
    `${category.parentId ? "— " : ""}${category.name}`;

  return (
    <div className="space-y-6">
      <section className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm font-bold text-brand-600">
            مدیریت فروش و معرفی {entityName}
          </p>
          <h1 className="mt-1 text-2xl font-black text-ink-950 sm:text-3xl">
            محصولات، خدمات و دسته‌بندی‌ها
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-7 text-slate-500">
            برای کالا، خدمات، پروژه‌های صنعتی یا پکیج‌های سازمانی یک فهرست
            ساخت‌یافته بسازید و آن را در معرفی عمومی شرکت منتشر کنید.
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <button
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700"
            onClick={() => setBranchesOpen(true)}
            type="button"
          >
            <AppIcon name="building" size={18} />
            شعبه‌ها
          </button>
          <button
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700"
            onClick={() => openCategoryEditor()}
            type="button"
          >
            <AppIcon name="grid" size={18} />
            دسته‌بندی جدید
          </button>
          <button
            data-guide-target="catalog-create"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 py-3 text-sm font-black text-white disabled:opacity-50"
            disabled={limitReached}
            onClick={() => openItemEditor()}
            type="button"
          >
            <AppIcon name="plus" size={18} />
            {limitReached ? "سقف ثبت تکمیل است" : "محصول یا خدمت جدید"}
          </button>
        </div>
      </section>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric label="همه موارد" value={faNumber(items.length)} />
        <Metric
          label="محصول"
          tone="text-brand-600"
          value={faNumber(
            items.filter((item) => item.itemType === "Product").length,
          )}
        />
        <Metric
          label="خدمت"
          tone="text-violet-600"
          value={faNumber(
            items.filter((item) => item.itemType === "Service").length,
          )}
        />
        <Metric
          label="منتشرشده"
          tone="text-emerald-600"
          value={faNumber(items.filter((item) => item.isPublished).length)}
        />
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="flex flex-col gap-3 lg:flex-row">
          <label className="relative flex-1">
            <AppIcon
              className="absolute right-3 top-3 text-slate-400"
              name="search"
              size={18}
            />
            <input
              className="input pr-10"
              onChange={(event) => setSearch(event.target.value)}
              placeholder="جست‌وجو در نام، SKU، بارکد، برند یا دسته‌بندی"
              value={search}
            />
          </label>
          <select
            className="input bg-white lg:w-52"
            onChange={(event) => setSelectedCategoryId(event.target.value)}
            value={selectedCategoryId}
          >
            <option value="all">همه دسته‌بندی‌ها</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {categoryTitle(category)}
              </option>
            ))}
          </select>
          <select
            className="input bg-white lg:w-44"
            onChange={(event) =>
              setSelectedType(event.target.value as "all" | ItemType)
            }
            value={selectedType}
          >
            <option value="all">همه نوع‌ها</option>
            <option value="Product">محصول</option>
            <option value="Service">خدمت</option>
          </select>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {categories.map((category) => (
            <button
              className={`rounded-full px-3 py-1.5 text-xs font-bold ${selectedCategoryId === category.id ? "bg-brand-600 text-white" : "bg-slate-100 text-slate-600"}`}
              key={category.id}
              onClick={() =>
                setSelectedCategoryId((current) =>
                  current === category.id ? "all" : category.id,
                )
              }
              type="button"
            >
              {categoryTitle(category)}{" "}
              <span className="mr-1 opacity-70">
                {faNumber(category.itemCount)}
              </span>
            </button>
          ))}
        </div>
      </section>
      {limitReached ? (
        <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">
          سقف ثبت محصول و خدمت این لایسنس تکمیل شده است. ویرایش و مدیریت دسته‌ها
          همچنان امکان‌پذیر است.
        </div>
      ) : null}

      {itemsQuery.isLoading ? (
        <LoadingBox label="در حال دریافت محصولات و خدمات…" />
      ) : null}
      {itemsQuery.isError ? (
        <ErrorBox text={errorText(itemsQuery.error)} />
      ) : null}
      {!itemsQuery.isLoading && !itemsQuery.isError ? (
        <section className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {filteredItems.map((item, index) => (
            <CatalogCard
              item={item}
              index={index}
              key={item.id}
              onDelete={() => {
                if (window.confirm(`«${item.name}» حذف شود؟`))
                  removeItem.mutate(item.id);
              }}
              onEdit={() => openItemEditor(item)}
              onInventory={() => setInventoryProduct(item)}
              onPreview={() => setPdfPreview(item)}
            />
          ))}
          {filteredItems.length === 0 ? (
            <button
              className="rounded-3xl border border-dashed border-brand-200 bg-brand-50/50 p-10 text-center md:col-span-2 xl:col-span-3"
              onClick={() => openItemEditor()}
              type="button"
            >
              <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-white text-brand-600 shadow-sm">
                <AppIcon name="box" size={22} />
              </span>
              <strong className="mt-4 block text-ink-950">
                {items.length
                  ? "موردی با این فیلتر پیدا نشد"
                  : "اولین محصول یا خدمت را ثبت کنید"}
              </strong>
              <span className="mt-2 block text-sm text-slate-500">
                قبل از ثبت می‌توانید دسته‌بندی بسازید.
              </span>
            </button>
          ) : null}
        </section>
      ) : null}

      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-bold text-brand-600">ساختار فهرست</p>
            <h2 className="mt-1 text-lg font-black text-ink-950">
              دسته‌بندی‌ها
            </h2>
            <p className="mt-1 text-xs leading-6 text-slate-400">
              دسته‌بندی را می‌توان دو سطحی تعریف کرد. حذف دسته، محصولات آن را
              حذف نمی‌کند و فقط ارتباطشان را پاک می‌کند.
            </p>
          </div>
          <button
            className="rounded-xl bg-brand-50 px-3 py-2 text-xs font-black text-brand-700"
            onClick={() => openCategoryEditor()}
            type="button"
          >
            افزودن دسته
          </button>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {categories.map((category) => (
            <article
              className={`rounded-2xl border p-4 ${category.isActive ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50 opacity-70"}`}
              key={category.id}
            >
              <div className="flex justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-black text-slate-800">
                    {categoryTitle(category)}
                  </h3>
                  <p className="mt-1 line-clamp-2 text-[11px] leading-5 text-slate-400">
                    {category.description || "بدون توضیحات"}
                  </p>
                </div>
                <span className="rounded-lg bg-brand-50 px-2 py-1 text-[11px] font-black text-brand-700">
                  {faNumber(category.itemCount)}
                </span>
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  className="rounded-lg bg-slate-100 px-2.5 py-1.5 text-[11px] font-bold text-slate-600"
                  onClick={() => openCategoryEditor(category)}
                  type="button"
                >
                  ویرایش
                </button>
                <button
                  className="rounded-lg bg-rose-50 px-2.5 py-1.5 text-[11px] font-bold text-rose-600"
                  onClick={() => {
                    if (window.confirm(`دسته «${category.name}» حذف شود؟`))
                      removeCategory.mutate(category.id);
                  }}
                  type="button"
                >
                  حذف
                </button>
              </div>
            </article>
          ))}
          {categories.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-slate-200 p-5 text-center text-sm text-slate-400 sm:col-span-2 xl:col-span-3">
              هنوز دسته‌بندی تعریف نشده است.
            </p>
          ) : null}
        </div>
      </section>

      {itemEditor ? (
        <Modal
          onClose={closeItemEditor}
          subtitle="نام، اطلاعات فروش، نمایش عمومی و فایل معرفی را برای این کالا یا خدمت ثبت کنید."
          title={
            itemEditor === "new"
              ? "افزودن محصول یا خدمت"
              : "ویرایش محصول یا خدمت"
          }
        >
          <UploadBox
            error={
              fileError ||
              (uploadFile.isError ? errorText(uploadFile.error) : "")
            }
            fileName={selectedFileName}
            loading={uploadFile.isPending}
            onClick={() => fileInputRef.current?.click()}
            onCameraClick={() => cameraInputRef.current?.click()}
          >
            <input
              accept="application/pdf,image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(event) => {
                chooseFile(event.target.files?.[0]);
                event.currentTarget.value = "";
              }}
              ref={fileInputRef}
              type="file"
            />
            <input
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              className="hidden"
              data-guide-target="catalog-camera"
              onChange={(event) => {
                chooseFile(event.target.files?.[0]);
                event.currentTarget.value = "";
              }}
              ref={cameraInputRef}
              type="file"
            />
          </UploadBox>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <Field label="نام">
              <input
                autoFocus
                className="input"
                onChange={(event) =>
                  setItemForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                value={itemForm.name}
              />
            </Field>
            <Field label="نوع">
              <select
                className="input bg-white"
                onChange={(event) =>
                  setItemForm((current) => ({
                    ...current,
                    itemType: event.target.value as ItemType,
                  }))
                }
                value={itemForm.itemType}
              >
                <option value="Product">محصول</option>
                <option value="Service">خدمت</option>
              </select>
            </Field>
            <Field label="دسته‌بندی">
              <select
                className="input bg-white"
                onChange={(event) =>
                  setItemForm((current) => ({
                    ...current,
                    categoryId: event.target.value,
                  }))
                }
                value={itemForm.categoryId}
              >
                <option value="">بدون دسته‌بندی</option>
                {activeCategories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {categoryTitle(category)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="کد / SKU">
              <input
                className="input font-sans"
                dir="ltr"
                onChange={(event) =>
                  setItemForm((current) => ({
                    ...current,
                    code: event.target.value,
                  }))
                }
                value={itemForm.code}
              />
            </Field>
            <Field label="برند یا تامین‌کننده">
              <input
                className="input"
                onChange={(event) =>
                  setItemForm((current) => ({
                    ...current,
                    brand: event.target.value,
                  }))
                }
                value={itemForm.brand}
              />
            </Field>
            <Field label="واحد فروش">
              <input
                className="input"
                onChange={(event) =>
                  setItemForm((current) => ({
                    ...current,
                    unit: event.target.value,
                  }))
                }
                value={itemForm.unit}
              />
            </Field>
            <Field label="وضعیت عرضه">
              <select
                className="input bg-white"
                onChange={(event) =>
                  setItemForm((current) => ({
                    ...current,
                    availabilityStatus: event.target.value as Availability,
                  }))
                }
                value={itemForm.availabilityStatus}
              >
                {Object.entries(availabilityLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="ترتیب نمایش">
              <input
                className="input font-sans"
                dir="ltr"
                min="0"
                onChange={(event) =>
                  setItemForm((current) => ({
                    ...current,
                    sortOrder: event.target.value,
                  }))
                }
                type="number"
                value={itemForm.sortOrder}
              />
            </Field>
            <Field label="قیمت">
              <input
                className="input font-sans"
                dir="ltr"
                min="0"
                onChange={(event) =>
                  setItemForm((current) => ({
                    ...current,
                    price: event.target.value,
                  }))
                }
                type="number"
                value={itemForm.price}
              />
            </Field>
            <Field label="واحد پول">
              <select
                className="input bg-white"
                onChange={(event) =>
                  setItemForm((current) => ({
                    ...current,
                    currency: event.target.value,
                  }))
                }
                value={itemForm.currency}
              >
                <option value="IRT">تومان</option>
                <option value="IRR">ریال</option>
                <option value="USD">دلار</option>
                <option value="EUR">یورو</option>
              </select>
            </Field>
            <Field className="sm:col-span-2" label="لینک فایل یا صفحه معرفی">
              <input
                className="input font-sans"
                dir="ltr"
                onChange={(event) =>
                  setItemForm((current) => ({
                    ...current,
                    catalogUrl: event.target.value,
                  }))
                }
                placeholder="https://..."
                value={itemForm.catalogUrl}
              />
            </Field>
            <Field className="sm:col-span-2" label="توضیحات">
              <textarea
                className="input min-h-24 resize-y"
                onChange={(event) =>
                  setItemForm((current) => ({
                    ...current,
                    description: event.target.value,
                  }))
                }
                value={itemForm.description}
              />
            </Field>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Toggle
              checked={itemForm.isPublished}
              label="انتشار در صفحه عمومی"
              onChange={(value) =>
                setItemForm((current) => ({ ...current, isPublished: value }))
              }
            />
            <Toggle
              checked={itemForm.isFeatured}
              label="نمایش به‌عنوان مورد ویژه"
              onChange={(value) =>
                setItemForm((current) => ({ ...current, isFeatured: value }))
              }
            />
          </div>
          {saveItem.isError ? (
            <p className="mt-4 text-xs font-bold text-rose-600">
              {errorText(saveItem.error)}
            </p>
          ) : null}
          <Actions
            disabled={
              saveItem.isPending ||
              uploadFile.isPending ||
              !itemForm.name.trim()
            }
            loading={saveItem.isPending}
            onCancel={closeItemEditor}
            onSave={() => saveItem.mutate()}
          />
        </Modal>
      ) : null}
      {categoryEditor ? (
        <Modal
          onClose={closeCategoryEditor}
          subtitle="دسته‌ها می‌توانند اصلی یا زیر‌دسته باشند."
          title={
            categoryEditor === "new" ? "افزودن دسته‌بندی" : "ویرایش دسته‌بندی"
          }
        >
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <Field label="نام دسته">
              <input
                autoFocus
                className="input"
                onChange={(event) =>
                  setCategoryForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                value={categoryForm.name}
              />
            </Field>
            <Field label="دسته والد">
              <select
                className="input bg-white"
                onChange={(event) =>
                  setCategoryForm((current) => ({
                    ...current,
                    parentId: event.target.value,
                  }))
                }
                value={categoryForm.parentId}
              >
                <option value="">دسته اصلی</option>
                {parentOptions.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="ترتیب نمایش">
              <input
                className="input font-sans"
                dir="ltr"
                min="0"
                onChange={(event) =>
                  setCategoryForm((current) => ({
                    ...current,
                    sortOrder: event.target.value,
                  }))
                }
                type="number"
                value={categoryForm.sortOrder}
              />
            </Field>
            <Field label="وضعیت">
              <select
                className="input bg-white"
                onChange={(event) =>
                  setCategoryForm((current) => ({
                    ...current,
                    isActive: event.target.value === "true",
                  }))
                }
                value={String(categoryForm.isActive)}
              >
                <option value="true">فعال</option>
                <option value="false">غیرفعال</option>
              </select>
            </Field>
            <Field className="sm:col-span-2" label="توضیحات">
              <textarea
                className="input min-h-24 resize-y"
                onChange={(event) =>
                  setCategoryForm((current) => ({
                    ...current,
                    description: event.target.value,
                  }))
                }
                value={categoryForm.description}
              />
            </Field>
          </div>
          {saveCategory.isError ? (
            <p className="mt-4 text-xs font-bold text-rose-600">
              {errorText(saveCategory.error)}
            </p>
          ) : null}
          <Actions
            disabled={saveCategory.isPending || !categoryForm.name.trim()}
            loading={saveCategory.isPending}
            onCancel={closeCategoryEditor}
            onSave={() => saveCategory.mutate()}
          />
        </Modal>
      ) : null}
      {branchesOpen ? (
        <BranchManagerModal
          branches={branchesQuery.data ?? []}
          error={
            saveBranch.isError
              ? errorText(saveBranch.error)
              : removeBranch.isError
                ? errorText(removeBranch.error)
                : null
          }
          loading={saveBranch.isPending || removeBranch.isPending}
          onClose={() => setBranchesOpen(false)}
          onDelete={(id) => removeBranch.mutate(id)}
          onSave={(id, request) => saveBranch.mutate({ id, request })}
        />
      ) : null}
      {inventoryProduct ? (
        <VariantInventoryModal
          branches={branchesQuery.data ?? []}
          error={
            saveVariant.isError
              ? errorText(saveVariant.error)
              : saveStock.isError
                ? errorText(saveStock.error)
                : removeVariant.isError
                  ? errorText(removeVariant.error)
                  : null
          }
          loading={
            saveVariant.isPending ||
            saveStock.isPending ||
            removeVariant.isPending
          }
          onClose={() => setInventoryProduct(null)}
          onDelete={(id) => removeVariant.mutate(id)}
          onSave={(id, request) => saveVariant.mutate({ id, request })}
          onStockSave={(variantId, request) =>
            saveStock.mutate({ variantId, request })
          }
          product={inventoryProduct}
          variants={variantsQuery.data ?? []}
        />
      ) : null}
      {pdfPreview?.catalogUrl ? (
        <PdfViewerModal
          onClose={() => setPdfPreview(null)}
          title={pdfPreview.name}
          url={pdfPreview.catalogUrl}
        />
      ) : null}
    </div>
  );
};

const BranchManagerModal = ({
  branches,
  loading,
  error,
  onClose,
  onSave,
  onDelete,
}: {
  branches: TenantBranch[];
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (id: string | undefined, request: UpsertTenantBranchRequest) => void;
  onDelete: (id: string) => void;
}): React.ReactElement => {
  const [editor, setEditor] = useState<TenantBranch | "new" | null>(null);
  const [form, setForm] = useState<UpsertTenantBranchRequest>({
    name: "",
    code: "",
    address: null,
    phone: null,
    isActive: true,
  });
  const open = (branch?: TenantBranch): void => {
    setEditor(branch ?? "new");
    setForm(
      branch
        ? {
            name: branch.name,
            code: branch.code,
            address: branch.address,
            phone: branch.phone,
            isActive: branch.isActive,
          }
        : { name: "", code: "", address: null, phone: null, isActive: true },
    );
  };
  return (
    <Modal
      onClose={onClose}
      subtitle="شعبه برای تفکیک موجودی و ثبت فروش کالاهای دارای واریانت استفاده می‌شود."
      title="مدیریت شعبه‌ها"
    >
      <div className="mt-5 flex justify-end">
        <button
          className="rounded-xl bg-brand-600 px-3 py-2 text-xs font-black text-white"
          onClick={() => open()}
          type="button"
        >
          افزودن شعبه
        </button>
      </div>
      {editor ? (
        <section className="mt-4 rounded-2xl border border-brand-100 bg-brand-50/40 p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="نام شعبه">
              <input
                autoFocus
                className="input"
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                value={form.name}
              />
            </Field>
            <Field label="کد شعبه">
              <input
                className="input font-sans"
                dir="ltr"
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    code: event.target.value,
                  }))
                }
                placeholder="MAIN"
                value={form.code}
              />
            </Field>
            <Field label="شماره تماس">
              <input
                className="input font-sans"
                dir="ltr"
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    phone: event.target.value || null,
                  }))
                }
                value={form.phone ?? ""}
              />
            </Field>
            <Field label="آدرس">
              <input
                className="input"
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    address: event.target.value || null,
                  }))
                }
                value={form.address ?? ""}
              />
            </Field>
          </div>
          <div className="mt-3">
            <Toggle
              checked={form.isActive}
              label="شعبه فعال است"
              onChange={(isActive) =>
                setForm((current) => ({ ...current, isActive }))
              }
            />
          </div>
          <div className="mt-4 flex gap-2">
            <button
              className="flex-1 rounded-xl border border-slate-200 bg-white py-2.5 text-xs font-bold"
              onClick={() => setEditor(null)}
              type="button"
            >
              انصراف
            </button>
            <button
              className="flex-1 rounded-xl bg-brand-600 py-2.5 text-xs font-black text-white disabled:opacity-50"
              disabled={loading || !form.name.trim() || !form.code.trim()}
              onClick={() =>
                onSave(editor === "new" ? undefined : editor.id, {
                  ...form,
                  name: form.name.trim(),
                  code: form.code.trim(),
                })
              }
              type="button"
            >
              ذخیره شعبه
            </button>
          </div>
        </section>
      ) : null}
      <div className="mt-5 space-y-2">
        {branches.map((branch) => (
          <article
            className="flex items-center gap-3 rounded-2xl border border-slate-200 p-3"
            key={branch.id}
          >
            <span className="grid size-10 place-items-center rounded-xl bg-slate-100 text-slate-600">
              <AppIcon name="building" size={18} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-black">{branch.name}</p>
              <p className="mt-1 font-sans text-[11px] text-slate-400">
                {branch.code}
                {branch.phone ? ` · ${branch.phone}` : ""}
              </p>
            </div>
            <span
              className={`rounded-lg px-2 py-1 text-[10px] font-black ${branch.isActive ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}
            >
              {branch.isActive ? "فعال" : "غیرفعال"}
            </span>
            <button
              className="rounded-lg bg-slate-100 px-2 py-1.5 text-[11px] font-bold"
              onClick={() => open(branch)}
              type="button"
            >
              ویرایش
            </button>
            <button
              className="rounded-lg bg-rose-50 px-2 py-1.5 text-[11px] font-bold text-rose-600"
              onClick={() => {
                if (window.confirm(`شعبه «${branch.name}» حذف شود؟`))
                  onDelete(branch.id);
              }}
              type="button"
            >
              حذف
            </button>
          </article>
        ))}
        {branches.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-slate-200 p-6 text-center text-xs font-bold text-slate-400">
            برای شروع، حداقل یک شعبه تعریف کنید.
          </p>
        ) : null}
      </div>
      {error ? (
        <p className="mt-4 rounded-xl bg-rose-50 p-3 text-xs font-bold text-rose-700">
          {error}
        </p>
      ) : null}
    </Modal>
  );
};

const VariantInventoryModal = ({
  product,
  variants,
  branches,
  loading,
  error,
  onClose,
  onSave,
  onDelete,
  onStockSave,
}: {
  product: CatalogItem;
  variants: CatalogVariant[];
  branches: TenantBranch[];
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (
    id: string | undefined,
    request: UpsertCatalogVariantRequest,
  ) => void;
  onDelete: (id: string) => void;
  onStockSave: (variantId: string, request: UpsertCatalogStockRequest) => void;
}): React.ReactElement => {
  const [editor, setEditor] = useState<CatalogVariant | "new" | null>(null);
  const [stockEditor, setStockEditor] = useState<CatalogVariant | null>(null);
  const [variantForm, setVariantForm] = useState<UpsertCatalogVariantRequest>({
    name: "",
    color: "",
    size: "",
    sku: "",
    barcode: "",
    priceOverride: null,
    isActive: true,
    sortOrder: 0,
  });
  const [stockForm, setStockForm] = useState<UpsertCatalogStockRequest>({
    branchId: "",
    onHandQuantity: 0,
    reorderPoint: 0,
    note: null,
  });
  const openVariant = (variant?: CatalogVariant): void => {
    setEditor(variant ?? "new");
    setVariantForm(
      variant
        ? {
            name: variant.name,
            color: variant.color,
            size: variant.size,
            sku: variant.sku,
            barcode: variant.barcode,
            priceOverride: variant.priceOverride,
            isActive: variant.isActive,
            sortOrder: variant.sortOrder,
          }
        : {
            name: "",
            color: "",
            size: "",
            sku: "",
            barcode: "",
            priceOverride: null,
            isActive: true,
            sortOrder: 0,
          },
    );
  };
  const openStock = (variant: CatalogVariant): void => {
    setStockEditor(variant);
    const existing = variant.stocks[0];
    setStockForm({
      branchId:
        existing?.branchId ??
        branches.find((branch) => branch.isActive)?.id ??
        "",
      onHandQuantity: existing?.onHandQuantity ?? 0,
      reorderPoint: existing?.reorderPoint ?? 0,
      note: null,
    });
  };
  const branchStocks = stockEditor?.stocks ?? [];
  return (
    <Modal
      onClose={onClose}
      subtitle={`رنگ، سایز، SKU، بارکد و موجودی شعبه‌ای «${product.name}» را مدیریت کنید.`}
      title="واریانت و موجودی کالا"
    >
      <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs leading-6 text-slate-500">
          برای ثبت موجودی ابتدا یک واریانت (مثلاً «استاندارد») و حداقل یک شعبه
          بسازید. فروش فقط از موجودی واریانت کم می‌شود.
        </p>
        <button
          className="rounded-xl bg-violet-600 px-3 py-2 text-xs font-black text-white"
          onClick={() => openVariant()}
          type="button"
        >
          افزودن واریانت
        </button>
      </div>
      {editor ? (
        <section className="mt-4 rounded-2xl border border-violet-100 bg-violet-50/40 p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="نام واریانت">
              <input
                autoFocus
                className="input"
                onChange={(event) =>
                  setVariantForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                placeholder="مثلاً مشکی / ۴۱"
                value={variantForm.name ?? ""}
              />
            </Field>
            <Field label="رنگ">
              <input
                className="input"
                onChange={(event) =>
                  setVariantForm((current) => ({
                    ...current,
                    color: event.target.value || null,
                  }))
                }
                value={variantForm.color ?? ""}
              />
            </Field>
            <Field label="سایز">
              <input
                className="input"
                onChange={(event) =>
                  setVariantForm((current) => ({
                    ...current,
                    size: event.target.value || null,
                  }))
                }
                value={variantForm.size ?? ""}
              />
            </Field>
            <Field label="SKU اختصاصی">
              <input
                className="input font-sans"
                dir="ltr"
                onChange={(event) =>
                  setVariantForm((current) => ({
                    ...current,
                    sku: event.target.value || null,
                  }))
                }
                value={variantForm.sku ?? ""}
              />
            </Field>
            <Field label="بارکد اختصاصی">
              <input
                className="input font-sans"
                dir="ltr"
                onChange={(event) =>
                  setVariantForm((current) => ({
                    ...current,
                    barcode: event.target.value || null,
                  }))
                }
                value={variantForm.barcode ?? ""}
              />
            </Field>
            <Field label="قیمت جایگزین">
              <input
                className="input font-sans"
                dir="ltr"
                min="0"
                onChange={(event) =>
                  setVariantForm((current) => ({
                    ...current,
                    priceOverride:
                      event.target.value === ""
                        ? null
                        : Math.max(0, Number(event.target.value) || 0),
                  }))
                }
                type="number"
                value={variantForm.priceOverride ?? ""}
              />
            </Field>
          </div>
          <div className="mt-3">
            <Toggle
              checked={variantForm.isActive}
              label="واریانت در فروش فعال است"
              onChange={(isActive) =>
                setVariantForm((current) => ({ ...current, isActive }))
              }
            />
          </div>
          <div className="mt-4 flex gap-2">
            <button
              className="flex-1 rounded-xl border border-slate-200 bg-white py-2.5 text-xs font-bold"
              onClick={() => setEditor(null)}
              type="button"
            >
              انصراف
            </button>
            <button
              className="flex-1 rounded-xl bg-violet-600 py-2.5 text-xs font-black text-white disabled:opacity-50"
              disabled={
                loading ||
                !(
                  variantForm.name?.trim() ||
                  variantForm.color?.trim() ||
                  variantForm.size?.trim()
                )
              }
              onClick={() =>
                onSave(editor === "new" ? undefined : editor.id, variantForm)
              }
              type="button"
            >
              ذخیره واریانت
            </button>
          </div>
        </section>
      ) : null}
      {stockEditor ? (
        <section className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-black text-emerald-900">
              موجودی «{stockEditor.name}»
            </h3>
            <button
              className="text-xs font-bold text-slate-500"
              onClick={() => setStockEditor(null)}
              type="button"
            >
              بستن
            </button>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <Field label="شعبه">
              <select
                className="input bg-white"
                onChange={(event) => {
                  const stock = branchStocks.find(
                    (row) => row.branchId === event.target.value,
                  );
                  setStockForm((current) => ({
                    ...current,
                    branchId: event.target.value,
                    onHandQuantity: stock?.onHandQuantity ?? 0,
                    reorderPoint: stock?.reorderPoint ?? 0,
                  }));
                }}
                value={stockForm.branchId}
              >
                <option value="">انتخاب شعبه</option>
                {branches
                  .filter((branch) => branch.isActive)
                  .map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label="موجودی فعلی">
              <input
                className="input font-sans"
                dir="ltr"
                min="0"
                onChange={(event) =>
                  setStockForm((current) => ({
                    ...current,
                    onHandQuantity: Math.max(
                      0,
                      Number(event.target.value) || 0,
                    ),
                  }))
                }
                type="number"
                value={stockForm.onHandQuantity}
              />
            </Field>
            <Field label="نقطه سفارش">
              <input
                className="input font-sans"
                dir="ltr"
                min="0"
                onChange={(event) =>
                  setStockForm((current) => ({
                    ...current,
                    reorderPoint: Math.max(0, Number(event.target.value) || 0),
                  }))
                }
                type="number"
                value={stockForm.reorderPoint ?? 0}
              />
            </Field>
          </div>
          <Field className="mt-3" label="یادداشت اصلاح موجودی">
            <input
              className="input"
              onChange={(event) =>
                setStockForm((current) => ({
                  ...current,
                  note: event.target.value || null,
                }))
              }
              value={stockForm.note ?? ""}
            />
          </Field>
          <button
            className="mt-4 w-full rounded-xl bg-emerald-600 py-2.5 text-xs font-black text-white disabled:opacity-50"
            disabled={loading || !stockForm.branchId}
            onClick={() => onStockSave(stockEditor.id, stockForm)}
            type="button"
          >
            ثبت موجودی شعبه
          </button>
        </section>
      ) : null}
      <div className="mt-5 space-y-3">
        {variants.map((variant) => (
          <article
            className="rounded-2xl border border-slate-200 p-4"
            key={variant.id}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-black text-slate-800">
                  {variant.name}
                </h3>
                <p className="mt-1 font-sans text-[11px] text-slate-400">
                  {variant.sku || variant.barcode || "بدون SKU و بارکد"}
                  {variant.priceOverride !== null
                    ? ` · قیمت اختصاصی: ${faNumber(variant.priceOverride)}`
                    : ""}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  className="rounded-lg bg-emerald-50 px-2.5 py-1.5 text-[11px] font-black text-emerald-700"
                  onClick={() => openStock(variant)}
                  type="button"
                >
                  موجودی
                </button>
                <button
                  className="rounded-lg bg-slate-100 px-2.5 py-1.5 text-[11px] font-bold"
                  onClick={() => openVariant(variant)}
                  type="button"
                >
                  ویرایش
                </button>
                <button
                  className="rounded-lg bg-rose-50 px-2.5 py-1.5 text-[11px] font-bold text-rose-600"
                  onClick={() => {
                    if (window.confirm(`واریانت «${variant.name}» حذف شود؟`))
                      onDelete(variant.id);
                  }}
                  type="button"
                >
                  حذف
                </button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {variant.stocks.map((stock) => (
                <span
                  className={`rounded-lg px-2 py-1 text-[11px] font-bold ${stock.isLowStock ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}
                  key={stock.id}
                >
                  {stock.branchName}: {faNumber(stock.onHandQuantity)}{" "}
                  {stock.isLowStock ? "(کم)" : ""}
                </span>
              ))}
              {variant.stocks.length === 0 ? (
                <span className="rounded-lg bg-rose-50 px-2 py-1 text-[11px] font-bold text-rose-600">
                  موجودی ثبت نشده
                </span>
              ) : null}
            </div>
          </article>
        ))}
        {variants.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-slate-200 p-7 text-center text-xs font-bold leading-6 text-slate-400">
            هنوز واریانتی تعریف نشده است. کالای بدون واریانت همچنان قابل فروش
            است؛ برای کنترل موجودی شعبه‌ای، واریانت بسازید.
          </p>
        ) : null}
      </div>
      {error ? (
        <p className="mt-4 rounded-xl bg-rose-50 p-3 text-xs font-bold text-rose-700">
          {error}
        </p>
      ) : null}
    </Modal>
  );
};

const CatalogCard = ({
  item,
  index,
  onEdit,
  onDelete,
  onInventory,
  onPreview,
}: {
  item: CatalogItem;
  index: number;
  onEdit: () => void;
  onDelete: () => void;
  onInventory: () => void;
  onPreview: () => void;
}): React.ReactElement => {
  const background = [
    "from-brand-500 to-violet-500",
    "from-sky-500 to-cyan-500",
    "from-amber-500 to-orange-500",
  ][index % 3];
  return (
    <article className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div
        className={`relative flex h-40 items-end overflow-hidden bg-gradient-to-bl p-5 text-white ${background}`}
      >
        {isImageUrl(item.catalogUrl) ? (
          <img
            alt=""
            className="absolute inset-0 size-full object-cover"
            src={resolveCatalogResourceUrl(item.catalogUrl!)}
          />
        ) : null}
        <span className="absolute inset-0 bg-gradient-to-t from-slate-950/65 via-slate-950/10 to-transparent" />
        <span className="relative grid size-12 place-items-center rounded-2xl bg-white/20">
          <AppIcon
            name={item.itemType === "Product" ? "box" : "sparkles"}
            size={24}
          />
        </span>
        <div className="relative mr-auto flex gap-1.5">
          <span className="rounded-full bg-white px-2.5 py-1 text-[10px] font-black text-slate-700">
            {typeLabels[item.itemType]}
          </span>
          {item.isFeatured ? (
            <span className="rounded-full bg-amber-300 px-2.5 py-1 text-[10px] font-black text-amber-950">
              ویژه
            </span>
          ) : null}
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="truncate text-base font-black text-ink-950">
              {item.name}
            </h2>
            <p className="mt-1 truncate text-[11px] font-bold text-slate-400">
              {item.categoryName ?? "بدون دسته‌بندی"}
              {item.code ? ` · ${item.code}` : ""}
            </p>
          </div>
          <span
            className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-black ${availabilityStyles[item.availabilityStatus]}`}
          >
            {availabilityLabels[item.availabilityStatus]}
          </span>
        </div>
        <p className="mt-3 line-clamp-2 min-h-12 text-xs leading-6 text-slate-500">
          {item.description || "بدون توضیحات"}
        </p>
        <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-bold text-slate-500">
          {item.brand ? (
            <span className="rounded-lg bg-slate-100 px-2 py-1">
              {item.brand}
            </span>
          ) : null}
          <span className="rounded-lg bg-slate-100 px-2 py-1">
            واحد: {item.unit}
          </span>
          {item.isPublished ? (
            <span className="rounded-lg bg-emerald-50 px-2 py-1 text-emerald-700">
              منتشر شده
            </span>
          ) : (
            <span className="rounded-lg bg-amber-50 px-2 py-1 text-amber-700">
              پیش‌نویس
            </span>
          )}
        </div>
        {item.price !== null ? (
          <p className="mt-4 text-sm font-black text-brand-600">
            {faNumber(item.price)}{" "}
            {item.currency === "IRR" ? "ریال" : item.currency}{" "}
            <span className="text-[11px] text-slate-400">/ {item.unit}</span>
          </p>
        ) : (
          <p className="mt-4 text-xs font-bold text-slate-400">قیمت ثبت نشده</p>
        )}
        <div className="mt-5 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
          <button
            className="flex-1 rounded-xl bg-slate-950 py-2.5 text-xs font-bold text-white"
            onClick={onEdit}
            type="button"
          >
            ویرایش
          </button>
          {item.itemType === "Product" ? (
            <button
              className="rounded-xl bg-violet-50 px-3 py-2.5 text-xs font-black text-violet-700"
              onClick={onInventory}
              type="button"
            >
              واریانت و موجودی
            </button>
          ) : null}
          {item.catalogUrl && isPdfUrl(item.catalogUrl) ? (
            <button
              aria-label="نمایش PDF"
              className="grid size-10 place-items-center rounded-xl bg-emerald-50 text-emerald-600"
              onClick={onPreview}
              type="button"
            >
              <AppIcon name="document" size={17} />
            </button>
          ) : null}
          {item.catalogUrl && !isPdfUrl(item.catalogUrl) ? (
            <a
              aria-label="مشاهده فایل یا صفحه"
              className="grid size-10 place-items-center rounded-xl bg-emerald-50 text-emerald-600"
              {...catalogLinkProps(item.catalogUrl)}
            >
              <AppIcon name="document" size={17} />
            </a>
          ) : null}
          <button
            aria-label="حذف"
            className="grid size-10 place-items-center rounded-xl bg-rose-50 text-rose-600"
            onClick={onDelete}
            type="button"
          >
            <AppIcon name="close" size={17} />
          </button>
        </div>
      </div>
    </article>
  );
};

const Metric = ({
  label,
  value,
  tone = "text-ink-950",
}: {
  label: string;
  value: string;
  tone?: string;
}): React.ReactElement => (
  <article className="rounded-2xl border border-slate-200 bg-white p-5">
    <p className="text-sm font-bold text-slate-500">{label}</p>
    <p className={`mt-2 text-2xl font-black ${tone}`}>{value}</p>
  </article>
);
const LoadingBox = ({ label }: { label: string }): React.ReactElement => (
  <div className="grid min-h-56 place-items-center rounded-3xl border border-slate-200 bg-white text-sm font-bold text-slate-400">
    {label}
  </div>
);
const ErrorBox = ({ text }: { text: string }): React.ReactElement => (
  <div className="rounded-2xl bg-rose-50 p-4 text-sm font-bold text-rose-700">
    {text}
  </div>
);
const Field = ({
  label,
  children,
  className = "",
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}): React.ReactElement => (
  <label className={className}>
    <span className="mb-2 block text-xs font-bold text-slate-500">{label}</span>
    {children}
  </label>
);
const Toggle = ({
  checked,
  label,
  onChange,
}: {
  checked: boolean;
  label: string;
  onChange: (value: boolean) => void;
}): React.ReactElement => (
  <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 p-3">
    <input
      checked={checked}
      className="size-4 accent-brand-600"
      onChange={(event) => onChange(event.target.checked)}
      type="checkbox"
    />
    <span className="text-sm font-bold text-slate-600">{label}</span>
  </label>
);
const Actions = ({
  disabled,
  loading,
  onCancel,
  onSave,
}: {
  disabled: boolean;
  loading: boolean;
  onCancel: () => void;
  onSave: () => void;
}): React.ReactElement => (
  <div
    className="fixed inset-x-0 bottom-0 z-[10001] flex gap-3 border-t border-slate-100 bg-white px-5 pb-[max(env(safe-area-inset-bottom),1rem)] pt-3 shadow-[0_-10px_30px_rgba(15,23,42,0.10)] backdrop-blur sm:static sm:z-auto sm:mt-7 sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none"
    data-catalog-mobile-actions="true"
    style={{ zIndex: 10001 }}
  >
    <button
      className="min-h-12 flex-1 touch-manipulation rounded-xl border border-slate-200 py-3 text-sm font-bold text-slate-600"
      onClick={onCancel}
      type="button"
    >
      انصراف
    </button>
    <button
      className="min-h-12 flex-1 touch-manipulation rounded-xl bg-brand-600 py-3 text-sm font-black text-white disabled:opacity-50"
      disabled={disabled}
      onClick={onSave}
      type="button"
    >
      {loading ? "در حال ذخیره…" : "ذخیره"}
    </button>
  </div>
);
const UploadBox = ({
  children,
  loading,
  onClick,
  onCameraClick,
  fileName,
  error,
}: {
  children: React.ReactNode;
  loading: boolean;
  onClick: () => void;
  onCameraClick: () => void;
  fileName: string;
  error: string;
}): React.ReactElement => (
  <div className="rounded-2xl border-2 border-dashed border-brand-200 bg-brand-50/40 p-5 text-center">
    {children}
    <span className="mx-auto grid size-11 place-items-center rounded-2xl bg-white text-brand-600 shadow-sm">
      <AppIcon name="document" size={20} />
    </span>
    <p className="mt-2 text-sm font-black text-slate-700">تصویر یا PDF معرفی</p>
    <p className="mt-1 text-xs text-slate-400">
      JPG، PNG، WebP یا PDF؛ حداکثر ۱۵ مگابایت
    </p>
    <div className="mt-3 flex flex-wrap justify-center gap-2">
      <button
        className="rounded-xl bg-brand-600 px-4 py-2 text-xs font-black text-white disabled:opacity-50"
        disabled={loading}
        onClick={onClick}
        type="button"
      >
        {loading ? "در حال بارگذاری…" : "انتخاب فایل"}
      </button>
      <button
        className="inline-flex items-center gap-1.5 rounded-xl border border-brand-200 bg-white px-4 py-2 text-xs font-black text-brand-700 disabled:opacity-50"
        disabled={loading}
        onClick={onCameraClick}
        type="button"
      >
        <AppIcon name="camera" size={16} />
        گرفتن عکس
      </button>
    </div>
    <p className="mt-2 text-[11px] text-slate-400">
      در موبایل، «گرفتن عکس» دوربین پشت گوشی را باز می‌کند.
    </p>
    {fileName ? (
      <p className="mt-2 break-all text-xs font-bold text-emerald-700">
        {fileName}
      </p>
    ) : null}
    {error ? (
      <p className="mt-2 text-xs font-bold text-rose-600">{error}</p>
    ) : null}
  </div>
);
const Modal = ({
  title,
  subtitle,
  children,
  onClose,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  onClose: () => void;
}): React.ReactElement => {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return createPortal(
    <div
      aria-label={title}
      aria-modal="true"
      className="crm-shell fixed inset-0 grid place-items-end bg-slate-950/40 sm:place-items-center sm:p-4"
      data-app-modal="catalog"
      dir="rtl"
      role="dialog"
      style={{ zIndex: 10000 }}
    >
      <button
        aria-label="بستن"
        className="absolute inset-0"
        onClick={onClose}
        type="button"
      />
      <section className="relative z-10 h-[100dvh] max-h-[100dvh] w-full overflow-y-auto overscroll-contain bg-white p-5 pb-36 shadow-2xl sm:h-auto sm:max-h-[94vh] sm:max-w-2xl sm:rounded-3xl sm:p-7">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-black text-ink-950">{title}</h2>
            <p className="mt-1 text-sm leading-6 text-slate-400">{subtitle}</p>
          </div>
          <button
            className="grid size-9 shrink-0 place-items-center rounded-xl bg-slate-50 text-slate-500"
            onClick={onClose}
            type="button"
          >
            <AppIcon name="close" size={18} />
          </button>
        </div>
        {children}
      </section>
    </div>,
    document.body,
  );
};
